import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { scrollToTarget } from "../lib/scroll";
import { EASE, Tag } from "./Reveal";
import { CTA } from "./CTA";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const STATS = [
  { value: "$5K", label: "Cabinets + granite, guaranteed" },
  { value: "3–5", label: "Days on site, typically" },
  { value: "$0", label: "For a detailed quote" },
];

export const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 1200], [0, 140]);
  const scale = useTransform(scrollY, [0, 1200], [1, 1.12]);

  return (
    <section className="relative pt-28 sm:pt-40 overflow-hidden" data-testid="hero-section">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-8">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.2 }}>
          <Tag>Kitchen & home renovation</Tag>
        </motion.div>

        <h1
          className="mt-8 font-display font-semibold text-ink tracking-[-0.03em] leading-[0.94] text-[clamp(2.9rem,8.4vw,9rem)]"
          data-testid="hero-headline"
        >
          <MaskedLine delay={0.3}>Your kitchen</MaskedLine>
          <MaskedLine delay={0.4}>
            <span className="text-teal">glow-up</span> might be
          </MaskedLine>
          <MaskedLine delay={0.5}>closer than</MaskedLine>
          <MaskedLine delay={0.6}>you think.</MaskedLine>
        </h1>

        <motion.div
          className="mt-12 sm:mt-16 grid lg:grid-cols-12 gap-8 items-end"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1, ease: EASE }}
        >
          <p className="lg:col-span-5 text-lg sm:text-xl text-ink/70 leading-snug max-w-md font-medium" data-testid="hero-subheadline">
            New cabinets and granite countertops. <span className="text-ink">$5K guaranteed</span> for up to 18 cabinets. Installed in just a few days.
          </p>
          <div className="lg:col-span-7 flex flex-wrap items-center gap-3 lg:justify-end">
            <CTA onClick={() => scrollToTarget("#quote-form")} testId="hero-get-quote-btn" className="min-w-[260px]">
              Get my free kitchen quote
            </CTA>
            <button
              onClick={() => scrollToTarget("#transformations")}
              data-testid="hero-see-transformations-btn"
              className="inline-flex items-center gap-2 font-display font-medium text-sm sm:text-base text-ink px-6 py-4 border border-ink/20 hover:border-ink transition-colors duration-300"
            >
              See the transformations
              <ArrowDown className="h-4 w-4" />
            </button>
          </div>
        </motion.div>
      </div>

      <motion.div
        className="relative mt-14 sm:mt-20 h-[62vh] sm:h-[78vh] overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1.4, delay: 1.2 }}
      >
        <motion.img
          src="/images/kitchen-hero.png"
          alt="A bright, newly renovated kitchen with white shaker cabinetry"
          className="absolute inset-0 h-full w-full object-cover"
          style={{ y, scale }}
        />
        <div className="absolute inset-x-0 bottom-0">
          <div className="mx-auto max-w-[1600px] px-5 sm:px-8">
            <div className="grid grid-cols-3 bg-ink text-bone max-w-3xl divide-x divide-bone/15" data-testid="hero-stats">
              {STATS.map((s) => (
                <div key={s.label} className="px-4 sm:px-7 py-5 sm:py-7">
                  <p className="font-display font-semibold text-2xl sm:text-4xl tracking-[-0.02em]">{s.value}</p>
                  <p className="mt-1.5 font-mono text-[10px] sm:text-[11px] uppercase tracking-[0.14em] text-bone/55">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
};
